import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule, MatSelectChange } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LucideAngularModule, X, Plus, Edit2, Trash2, AlertCircle, AlertTriangle, FileText, User, Calendar, History, File, Check, SquarePen, Info } from 'lucide-angular';
import { MobileFrequenciaService } from '../../services/mobile-frequencia.service';
import { PermissionsService } from '@core/auth/services/permissions.service';
import type {
  Abono,
  Justificativa,
  MotivoAbonoJustificativa,
  FaltaDia,
  AbonoJustificativaListItem,
  FaltasRestantesItem
} from '../../services/mobile-frequencia.service';
import {
  calcularFaltasRestantes,
  calcularTodasFaltasSelecionadas,
  extrairMensagemErro,
  formatarDataParaDescricao,
  formatarFaltaParaExibicao,
  montarDataExclusaoLabel,
  montarFaltasParaSelecao,
  montarFaltasSelecionadasPayload,
  montarMensagemFaltasRestantes,
  obterArrayFaltasFormatadas,
  obterDatasFormatadas,
  processarSelecaoMatSelectFaltas
} from './abono-justificativa-modal.utils';
import { AbonoJustificativaDetalhesModalComponent } from './abono-justificativa-detalhes-modal.component';

/**
 * Modal de Abono e Justificativa de Faltas.
 * Baseado no AngularJS: tab-motivos.directive.js, app-select-motivo.js, modal-abono-justificativa.service.js
 */
@Component({
  selector: 'app-abono-justificativa-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSelectModule, MatFormFieldModule, LucideAngularModule, AbonoJustificativaDetalhesModalComponent],
  templateUrl: './abono-justificativa-modal.component.html',
  styleUrls: ['./abono-justificativa-modal.component.scss']
})
export class AbonoJustificativaModalComponent implements OnInit {
  /** Container com `overflow-y-auto` — único elemento que deve receber scroll */
  @ViewChild('modalScrollHost') modalScrollHost?: ElementRef<HTMLDivElement>;

  XIcon = X;
  PlusIcon = Plus;
  Edit2Icon = Edit2;
  Trash2Icon = Trash2;
  AlertCircleIcon = AlertCircle;
  AlertTriangleIcon = AlertTriangle;
  FileTextIcon = FileText;
  UserIcon = User;
  CalendarIcon = Calendar;
  HistoryIcon = History;
  FileIcon = File;
  CheckIcon = Check;
  SquarePenIcon = SquarePen;
  InfoIcon = Info;

  @Input() isOpen = false;
  @Input() enturmacaoId!: number;
  @Input() alunoNome = '';
  @Input() turmaNome = '';
  @Input() tipoFrequencia: 'DIA' | 'AULA' = 'DIA';
  @Input() periodoAvaliativoId!: number;
  @Input() periodoDescricao = '';
  @Input() itemEducacionalIds: number[] = [];
  @Input() isEjaModular = false;
  @Input() turmaId!: number;

  @Output() fechado = new EventEmitter<void>();
  @Output() salvo = new EventEmitter<{
    action: 'create' | 'update' | 'delete';
    tipo: 'ABONO' | 'JUSTIFICATIVA';
  }>();

  form = {
    tipo: 'ABONO' as 'ABONO' | 'JUSTIFICATIVA',
    faltasDiasSelecionadas: [] as number[],
    motivoId: null as number | null,
    observacao: ''
  };

  modoEdicao = false;
  itemEditandoId: number | null = null;
  itemEditandoTipo: 'ABONO' | 'JUSTIFICATIVA' | null = null;
  salvando = false;
  excluindo = false;
  carregandoLista = false;
  erroGlobal = '';
  todasFaltasSelecionadas = false;
  motivoDropdownAberto = false;
  datasDropdownAberto = false;
  dialogConfirmacaoAberto = false;
  itemParaExcluir: AbonoJustificativaListItem | null = null;
  modalDetalhesAberto = false;
  itemDetalhes: AbonoJustificativaListItem | null = null;

  motivos: MotivoAbonoJustificativa[] = [];
  faltasSemAbono: FaltaDia[] = [];
  faltasDoItemEmEdicao: NonNullable<AbonoJustificativaListItem['faltas']> = [];
  listaAbonosJustificativas: AbonoJustificativaListItem[] = [];
  /**
   * Lista usada pelo mat-select de faltas. Recalculada só em `atualizarFaltasParaSelecao()`
   * (evita recomputar/ordenar a cada ciclo de detecção no template).
   */
  faltasParaSelecao: FaltaDia[] = [];

  totalFaltas = 0;
  faltasDisponiveis = 0;

  // Faltas restantes: validação cruzada com o sistema Educação.
  hasFaltaEducacao = false;
  faltasRestantes = -1; // -1 = ainda não carregado
  mensagemFaltasRestantes = '';

  constructor(
    private frequenciaService: MobileFrequenciaService,
    private permissionsService: PermissionsService
  ) {}

  @HostListener('document:click')
  onDocumentClick(): void {
    this.datasDropdownAberto = false;
    this.motivoDropdownAberto = false;
  }

  async ngOnInit() {

    if (this.isOpen) {
      await this.inicializar();
    }
  }

  async inicializar() {
    // Timestamp inicial usado para garantir um loading mínimo (evita flash de conteúdo).
    const inicioOperacao = Date.now();

    try {
      this.carregandoLista = true;
      this.erroGlobal = '';

      await new Promise(resolve => setTimeout(resolve, 50));

      await Promise.all([
        this.carregarMotivos(),
        this.carregarFaltasSemAbono(),
        this.carregarListaAbonosJustificativas()
      ]);

      this.calcularEstatisticas();

      // Precisa vir depois de faltasSemAbono, usado na comparação.
      await this.carregarFaltasRestantes();

      const tempoDecorrido = Date.now() - inicioOperacao;
      const delayMinimo = 600;
      if (tempoDecorrido < delayMinimo) {
        await new Promise(resolve => setTimeout(resolve, delayMinimo - tempoDecorrido));
      }
    } catch (error: any) {
      this.mostrarErro('Erro ao carregar dados. Tente novamente.');
    } finally {
      this.carregandoLista = false;
    }
  }

  async carregarMotivos() {
    const response = await this.frequenciaService.getMotivosAbonoJustificativa();
    this.motivos = response.content; // response é paginado; content é o array
  }

  async carregarFaltasSemAbono() {
    if (this.tipoFrequencia === 'AULA') {
      const idsParaFiltro = (!this.isEjaModular && this.itemEducacionalIds.length > 0)
        ? this.itemEducacionalIds
        : undefined;
      this.faltasSemAbono = await this.frequenciaService.getFaltasAulasSemAbonos(
        this.enturmacaoId,
        this.periodoAvaliativoId,
        idsParaFiltro
      );
    } else {
      this.faltasSemAbono = await this.frequenciaService.getFaltasDiasSemAbonos(
        this.enturmacaoId,
        this.periodoAvaliativoId
      );
    }
    this.atualizarFaltasParaSelecao();
  }

  async carregarListaAbonosJustificativas() {
    const idsParaFiltro = (!this.isEjaModular && this.itemEducacionalIds.length > 0)
      ? this.itemEducacionalIds
      : undefined;
    const response = this.tipoFrequencia === 'AULA'
      ? await this.frequenciaService.getAbonosJustificativasFaltasAulas(
          this.enturmacaoId,
          this.periodoAvaliativoId,
          idsParaFiltro,
          this.isEjaModular
        )
      : await this.frequenciaService.getAbonosJustificativasFaltasDias(
          this.enturmacaoId,
          this.periodoAvaliativoId
        );
    this.listaAbonosJustificativas = response.content;
  }

  calcularEstatisticas() {
    const faltasJaProcessadas = this.listaAbonosJustificativas.reduce(
      (total, item) => total + (item.faltas?.length || 1), 0
    );
    this.totalFaltas = this.faltasSemAbono.length + faltasJaProcessadas;
    this.faltasDisponiveis = this.faltasSemAbono.length;
  }

  /**
   * Carrega faltas restantes (não justificadas) para detectar abonos feitos no sistema Educação.
   * AngularJS: app-faltas-restantes.directive.js → refresh() + findFaltasRestantes()
   */
  async carregarFaltasRestantes() {
    try {
      let itensEducacionais: number | number[] | undefined;

      if (this.tipoFrequencia === 'AULA') {
        if (this.itemEducacionalIds?.length > 0) {
          itensEducacionais = this.itemEducacionalIds;
        }
      } else {
        // Falta-dia não traz itemEducacionalPeriodo pronto: precisa buscar via registros-faltas.
        if (this.turmaId) {
          const registros = await this.frequenciaService.getRegistrosFaltas(
            this.turmaId, this.enturmacaoId, this.periodoAvaliativoId
          );
          const itemEdPeriodo = registros?.[0]?.itemEducacional;
          if (itemEdPeriodo?.id) {
            itensEducacionais = itemEdPeriodo.id;
          }
        }
      }

      if (!itensEducacionais) {
        console.log('⚠️ [Modal] Sem itensEducacionais para buscar faltas restantes');
        this.montarMensagemFaltasRestantes();
        return;
      }

      const data = await this.frequenciaService.getFaltasRestantes(
        this.enturmacaoId, this.periodoAvaliativoId, itensEducacionais
      );

      this.calcularFaltasRestantes(data);
    } catch (error) {
      console.error('❌ [Modal] Erro ao carregar faltas restantes:', error);
      this.montarMensagemFaltasRestantes();
    }
  }

  /**
   * Calcula faltas restantes comparando com dados do endpoint nao-justificadas.
   * Detecta se há faltas registradas no sistema Educação.
   * AngularJS: app-faltas-restantes.directive.js → getTotal() + getDescricao()
   */
  calcularFaltasRestantes(data: FaltasRestantesItem[]) {
    const qtdFaltasSemAbono = this.faltasSemAbono.length;
    const resultado = calcularFaltasRestantes(data, qtdFaltasSemAbono, this.hasFaltaEducacao);
    this.faltasRestantes = resultado.faltasRestantes;
    this.hasFaltaEducacao = resultado.hasFaltaEducacao;

    this.faltasDisponiveis = this.faltasRestantes;

    this.montarMensagemFaltasRestantes();

    console.log('📊 [Modal] Faltas restantes calculadas:', {
      qtdFaltasSemAbono,
      faltasRestantes: this.faltasRestantes,
      hasFaltaEducacao: this.hasFaltaEducacao,
      mensagem: this.mensagemFaltasRestantes
    });
  }

  /**
   * Monta a mensagem descritiva para o banner de faltas restantes.
   * AngularJS: app-faltas-restantes.directive.js → getDescricao()
   */
  private montarMensagemFaltasRestantes() {
    this.mensagemFaltasRestantes = montarMensagemFaltasRestantes(
      this.faltasRestantes,
      this.faltasSemAbono.length,
      this.hasFaltaEducacao
    );
  }

  get motivosFiltrados(): MotivoAbonoJustificativa[] {
    return this.motivos.filter(m => m.tipo === this.form.tipo);
  }

  /**
   * Atualiza `faltasParaSelecao` quando mudam: lista da API, modo edição ou faltas do item em edição.
   */
  private atualizarFaltasParaSelecao(): void {
    this.faltasParaSelecao = montarFaltasParaSelecao(
      this.faltasSemAbono,
      this.faltasDoItemEmEdicao,
      this.modoEdicao,
      this.enturmacaoId
    );
  }

  onTipoChange() {
    const motivoAtual = this.motivos.find(m => m.id === this.form.motivoId);
    if (motivoAtual && motivoAtual.tipo !== this.form.tipo) {
      this.form.motivoId = null;
    }
    this.motivoDropdownAberto = false;
  }

  onFaltaToggle(faltaId: number) {
    const index = this.form.faltasDiasSelecionadas.indexOf(faltaId);
    if (index === -1) {
      this.form.faltasDiasSelecionadas.push(faltaId);
    } else {
      this.form.faltasDiasSelecionadas.splice(index, 1);
    }

    this.atualizarFlagTodasFaltas();
  }

  /** AngularJS: seleção marca/desmarca todas as faltas. */
  toggleTodasFaltas() {
    if (this.todasFaltasSelecionadas) {
      this.form.faltasDiasSelecionadas = [];
      this.todasFaltasSelecionadas = false;
    } else {
      this.form.faltasDiasSelecionadas = this.faltasParaSelecao.map(f => f.id);
      this.todasFaltasSelecionadas = true;
    }
  }

  private atualizarFlagTodasFaltas() {
    this.todasFaltasSelecionadas = calcularTodasFaltasSelecionadas(this.faltasParaSelecao, this.form.faltasDiasSelecionadas);
  }

  /** Intercepta o valor especial 'TODAS' do mat-select pra marcar/desmarcar todas as faltas. */
  onFaltasMatSelectChange(event: MatSelectChange) {
    const resultado = processarSelecaoMatSelectFaltas(event.value, this.todasFaltasSelecionadas, this.faltasParaSelecao);
    this.form.faltasDiasSelecionadas = resultado.faltasSelecionadas;
    this.todasFaltasSelecionadas = resultado.todasSelecionadas;
  }

  faltasTriggerText(): string {
    return this.obterDatasFormatadas();
  }

  /** Mixed array: pode incluir o valor especial 'TODAS' junto com ids numéricos. */
  getFaltasMatSelectValue(): (string | number)[] {
    if (this.todasFaltasSelecionadas) {
      return (['TODAS'] as (string | number)[]).concat(this.form.faltasDiasSelecionadas);
    }
    return this.form.faltasDiasSelecionadas;
  }

  onMotivoMatSelectChange(motivoId: number): void {
    const motivo = this.motivosFiltrados.find(m => m.id === motivoId);
    if (motivo) {
      this.selecionarMotivo(motivo);
    }
  }

  selecionarMotivo(motivo: MotivoAbonoJustificativa) {
    this.form.motivoId = motivo.id;
    this.motivoDropdownAberto = false;
  }

  formularioValido(): boolean {
    return (
      this.form.faltasDiasSelecionadas.length > 0 &&
      this.form.motivoId !== null
    );
  }

  private getPageMappingByTipo(tipo: 'ABONO' | 'JUSTIFICATIVA'): string {
    return tipo === 'ABONO' ? 'AbonoFaltaPageMapping' : 'JustificativaFaltaPageMapping';
  }

  private canUseOperation(tipo: 'ABONO' | 'JUSTIFICATIVA', operation: 'criar' | 'alterar' | 'remover'): boolean {
    const pageId = this.getPageMappingByTipo(tipo);
    // checkPermissionForStateName é permissivo em standalone (sem AngularJS), consistente com a diretiva.
    return this.permissionsService.checkPermissionForStateName(pageId) &&
      !this.permissionsService.isRevokedOperation(pageId, operation);
  }

  canSaveCurrentForm(): boolean {
    const operation: 'criar' | 'alterar' = this.modoEdicao ? 'alterar' : 'criar';
    return this.canUseOperation(this.form.tipo, operation);
  }

  canEditItem(item: AbonoJustificativaListItem): boolean {
    return this.canUseOperation(item.tipo, 'alterar');
  }

  canDeleteItem(item: AbonoJustificativaListItem): boolean {
    return this.canUseOperation(item.tipo, 'remover');
  }

  async salvar() {
    if (!this.formularioValido() || !this.canSaveCurrentForm()) return;

    const inicioOperacao = Date.now();

    try {
      this.salvando = true;
      this.erroGlobal = '';

      // Aguarda um ciclo pra garantir que o Angular renderize o loading.
      await new Promise(resolve => setTimeout(resolve, 50));

      const motivo = this.motivos.find(m => m.id === this.form.motivoId);
      if (!motivo) {
        this.mostrarErro('Motivo inválido');
        this.salvando = false;
        return;
      }

      // Captura antes de limparFormulario() resetar o estado.
      const tipoAtual = this.form.tipo;
      const isUpdate = this.modoEdicao;
      if (this.form.tipo === 'ABONO') {
        await this.salvarAbono(motivo);
      } else {
        await this.salvarJustificativa(motivo);
      }

      await Promise.all([
        this.carregarFaltasSemAbono(),
        this.carregarListaAbonosJustificativas()
      ]);

      this.calcularEstatisticas();
      await this.carregarFaltasRestantes();

      const tempoDecorrido = Date.now() - inicioOperacao;
      const delayMinimo = 800;
      if (tempoDecorrido < delayMinimo) {
        await new Promise(resolve => setTimeout(resolve, delayMinimo - tempoDecorrido));
      }

      this.limparFormulario();

      const eventoEmitido = {
        action: (isUpdate ? 'update' : 'create') as ('update' | 'create'),
        tipo: tipoAtual
      };

      this.salvo.emit(eventoEmitido);
    } catch (error: any) {
      this.mostrarErro(extrairMensagemErro(error, 'Erro ao salvar. Verifique os dados e tente novamente.'));
    } finally {
      this.salvando = false;
    }
  }

  async salvarAbono(motivo: MotivoAbonoJustificativa) {
    // Em modo AULA a data fica em alocacaoAula.data, não no top-level.
    const faltasDias = montarFaltasSelecionadasPayload(this.form.faltasDiasSelecionadas, this.faltasParaSelecao);

    // A API usa campos diferentes por tipo: faltasAulas (AULA) ou faltasDias (DIA)
    const abono: Abono = {
      id: this.itemEditandoId ?? undefined,
      enturmacao: { id: this.enturmacaoId },
      motivo: { id: motivo.id, tipo: motivo.tipo, descricao: motivo.descricao },
      ...(this.tipoFrequencia === 'AULA' ? { faltasAulas: faltasDias } : { faltasDias }),
      observacao: this.form.observacao || undefined,
      tipoAbono: this.tipoFrequencia === 'AULA' ? 'ABONO_FREQUENCIA_AULA' : 'ABONO_FREQUENCIA_DIA'
    };

    await this.frequenciaService.saveAbono(abono);
  }

  async salvarJustificativa(motivo: MotivoAbonoJustificativa) {
    const faltasDias = montarFaltasSelecionadasPayload(this.form.faltasDiasSelecionadas, this.faltasParaSelecao);

    // A API usa campos diferentes por tipo: faltas (AULA) ou faltasDias (DIA)
    const justificativa: Justificativa = {
      id: this.itemEditandoId ?? undefined,
      enturmacao: { id: this.enturmacaoId },
      motivo: { id: motivo.id, tipo: motivo.tipo, descricao: motivo.descricao },
      ...(this.tipoFrequencia === 'AULA' ? { faltas: faltasDias } : { faltasDias }),
      justificativa: this.form.observacao,
      tipoJustificativa: this.tipoFrequencia === 'AULA' ? 'JUSTIFICATIVA_FREQUENCIA_AULA' : 'JUSTIFICATIVA_FREQUENCIA_DIA'
    };

    await this.frequenciaService.saveJustificativa(justificativa);
  }

  editarItem(item: AbonoJustificativaListItem) {
    if (!this.canEditItem(item)) {
      return;
    }
    this.modoEdicao = true;
    // abonosJustificativasId é o ID real do abono/justificativa; item.id é o ID da falta-dia.
    this.itemEditandoId = item.abonosJustificativasId ?? item.id;
    this.itemEditandoTipo = item.tipo;
    this.form.tipo = item.tipo;
    this.form.faltasDiasSelecionadas = item.faltas?.map(f => f.id) || [];
    this.form.motivoId = item.motivo?.id || null;
    this.form.observacao = item.observacao || '';

    // Faltas do item já vinculadas a ele não estão em faltasSemAbono; guardadas à parte pros checkboxes.
    this.faltasDoItemEmEdicao = item.faltas || [];
    this.atualizarFaltasParaSelecao();
    this.atualizarFlagTodasFaltas();
    this.scrollModalScrollHostToTop('auto');
  }

  cancelarEdicao() {
    this.limparFormulario();
  }

  confirmarExclusao(item: AbonoJustificativaListItem) {
    if (!this.canDeleteItem(item)) {
      return;
    }
    this.itemParaExcluir = item;
    this.dialogConfirmacaoAberto = true;
  }

  getDataExclusaoLabel(item: AbonoJustificativaListItem | null): string {
    return montarDataExclusaoLabel(item, this.tipoFrequencia);
  }

  cancelarExclusao() {
    this.dialogConfirmacaoAberto = false;
    this.itemParaExcluir = null;
  }

  async confirmarExclusaoFinal() {
    if (!this.itemParaExcluir) return;
    if (!this.canDeleteItem(this.itemParaExcluir)) {
      return;
    }

    const item = this.itemParaExcluir;
    const inicioOperacao = Date.now();

    try {
      this.excluindo = true;
      this.erroGlobal = '';

      await new Promise(resolve => setTimeout(resolve, 50));

      const idParaExcluir = item.abonosJustificativasId ?? item.id;

      if (item.tipo === 'ABONO') {
        await this.frequenciaService.removeAbono(idParaExcluir);
      } else {
        await this.frequenciaService.removeJustificativa(idParaExcluir);
      }

      await Promise.all([
        this.carregarFaltasSemAbono(),
        this.carregarListaAbonosJustificativas()
      ]);

      this.calcularEstatisticas();
      await this.carregarFaltasRestantes();

      const tempoDecorrido = Date.now() - inicioOperacao;
      const delayMinimo = 800;
      if (tempoDecorrido < delayMinimo) {
        await new Promise(resolve => setTimeout(resolve, delayMinimo - tempoDecorrido));
      }

      const eventoEmitido = {
        action: 'delete' as const,
        tipo: item.tipo
      };

      this.salvo.emit(eventoEmitido);

      this.dialogConfirmacaoAberto = false;
      this.itemParaExcluir = null;
    } catch (error: any) {
      this.mostrarErro(extrairMensagemErro(error, 'Erro ao excluir. Tente novamente.'));
    } finally {
      this.excluindo = false;
    }
  }

  limparFormulario() {
    this.form = {
      tipo: 'ABONO',
      faltasDiasSelecionadas: [],
      motivoId: null,
      observacao: ''
    };
    this.modoEdicao = false;
    this.itemEditandoId = null;
    this.itemEditandoTipo = null;
    this.faltasDoItemEmEdicao = [];
    this.atualizarFaltasParaSelecao();
    this.todasFaltasSelecionadas = false;
    this.motivoDropdownAberto = false;
    this.datasDropdownAberto = false;
  }

  getMotivoSelecionado(): MotivoAbonoJustificativa | undefined {
    return this.motivos.find(m => m.id === this.form.motivoId);
  }

  abrirDetalhes(item: AbonoJustificativaListItem): void {
    this.itemDetalhes = item;
    this.modalDetalhesAberto = true;
  }

  fecharDetalhes(): void {
    this.modalDetalhesAberto = false;
    this.itemDetalhes = null;
  }

  /**
   * Formata data YYYY-MM-DD para DD/MM (ex: "2025-06-23" -> "23/06")
   */
  formatarDataParaDescricao(data: string): string {
    return formatarDataParaDescricao(data);
  }

  /**
   * Formata uma falta para exibição no dropdown
   * Modo DIA:  "06/11/2024"
   * Modo AULA: "06/11 - Aula 1 - História do Brasil"
   *
   * Nota: o endpoint /falta-aula retorna a data dentro de alocacaoAula.data
   * (não no campo top-level data). Por isso usamos fallback defensivo.
   */
  formatarFaltaParaExibicao(falta: FaltaDia): string {
    return formatarFaltaParaExibicao(falta, this.tipoFrequencia);
  }

  obterDatasFormatadas(): string {
    return obterDatasFormatadas(this.form.faltasDiasSelecionadas, this.todasFaltasSelecionadas, this.faltasParaSelecao, this.tipoFrequencia);
  }

  /**
   * Retorna array de strings formatadas para exibição em badges no histórico.
   * Modo DIA:  "06/11/2024"
   * Modo AULA: "03/02 - Aula 5 - Matemática"
   * (a data fica em alocacaoAula.data e o itemEducacional no nível raiz)
   */
  obterArrayFaltasFormatadas(faltas: AbonoJustificativaListItem['faltas']): string[] {
    return obterArrayFaltasFormatadas(faltas, this.tipoFrequencia);
  }

  truncarTexto(texto: string, maxLength = 80): string {
    if (!texto || texto.length <= maxLength) {
      return texto;
    }
    return texto.substring(0, maxLength) + '...';
  }

  trackById(index: number, item: AbonoJustificativaListItem): number {
    return item.abonosJustificativasId ?? item.id;
  }

  mostrarErro(mensagem: string) {
    this.erroGlobal = mensagem;

    this.scrollModalScrollHostToTop('smooth');
  }

  /** Rola o container com overflow da modal para o topo. */
  private scrollModalScrollHostToTop(behavior: ScrollBehavior): void {
    const el = this.modalScrollHost?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollTo({ top: 0, behavior });
  }

  fechar() {
    this.limparFormulario();
    this.fechado.emit();
  }
}
